import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronDown } from "react-icons/fa";
import ClientOnly from "~/components/shared-components/ClentOnly";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "~/components/ui/accordion";
import { Checkbox } from "~/components/ui/checkbox";
import { useFontStyle } from "~/hook/useFontStyle";
import { KirtanLyricsFilterAccordionProps } from "~/interface/kirtan-lyrics/kirtan-lyrics";
import { cn } from "~/lib/utils";
import { NOT_A } from "~/utils/constant";

const KirtanFilterAccordion = ({
  data,
  title,
  selectedFilter,
  setSelectedFilterId,
  isClear,
  setIsClear,
  value,
}: KirtanLyricsFilterAccordionProps) => {
  const [checkedItems, setCheckedItems] = useState<{ [key: string]: boolean }>({});
  const { normalInter, medium, mediumInter } = useFontStyle();
  const { t } = useTranslation();

  const [showAll, setShowAll] = useState(false);

  const visibleItems = showAll ? data : data.slice(0, 5);
  const shouldShowToggle = data.length > 5;

  useEffect(() => {
    const newChecked: { [key: string]: boolean } = {};
    selectedFilter.forEach((item) => {
      newChecked[item.id] = true;
    });
    setCheckedItems(selectedFilter.length > 0 ? newChecked : {});
  }, [selectedFilter]);

  useEffect(() => {
    isClear && setCheckedItems({});
  }, [isClear]);

  return (
    <Accordion
      type="single"
      collapsible
      defaultValue="rachayta" // dynamically control the open item
      className="w-full border-[1px] border-borderPrimary rounded-xl p-4 bg-primary"
    >
      <AccordionItem value={value} className="w-full">
        <AccordionTrigger className="w-full flex justify-between items-center" iconVisible={false}>
          <div className="w-full flex justify-between items-center">
            <p className={cn("body_text_m  text-textColor", medium)}>{title}</p>
            <FaChevronDown
              size={15}
              color={"#572713"}
              className={`transition-transform duration-300 shrink-0 text-muted-foreground rotate-180 group-data-[state=open]:rotate-0`}
            />
          </div>
        </AccordionTrigger>
        <AccordionContent className="w-full">
          <div className="w-full mt-2 pt-4 border-t-2 border-t-borderPrimary flex flex-col justify-start items-start gap-2">
            {visibleItems.map((item) => {
              const checkboxId = `checkbox-${item.id}`;
              return (
                <div className="flex items-center space-x-2" key={item.id}>
                  <div className="flex flex-row justify-start items-center space-x-3 space-y-0">
                    <ClientOnly
                      fallback={
                        <>
                          <div className="peer w-4 h-4 border-[1px] border-borderPrimary rounded-sm bg-none shrink-0" />
                          <label className={cn("text-14 text-textColor line-clamp-1 cursor-pointer", normalInter)} title={item.text}>
                            {item.text || NOT_A}
                          </label>
                        </>
                      }
                    >
                      <div className="flex items-center space-x-2" key={item.id}>
                        <div
                          className="flex flex-row justify-start items-center space-x-3 space-y-0 cursor-pointer"
                          onClick={() => {
                            const newChecked = !checkedItems[item.id];
                            setSelectedFilterId(item);
                            setIsClear();
                            setCheckedItems((prev) => ({
                              ...prev,
                              [item.id]: newChecked,
                            }));
                          }}
                        >
                          <Checkbox
                            id={checkboxId}
                            checked={!!checkedItems[item.id]}
                            onClick={(e) => e.stopPropagation()} // prevent double-toggle
                            className="border-borderPrimary data-[state=checked]:bg-textColor pointer-events-none"
                          />
                          <span className={cn("body_text_xs text-textColor line-clamp-1", normalInter)} title={item.text}>
                            {item.text || NOT_A}
                          </span>
                        </div>
                      </div>
                    </ClientOnly>
                  </div>
                </div>
              );
            })}

            {shouldShowToggle && (
              <button onClick={() => setShowAll((prev) => !prev)} className={cn("body_text_xs text-textColor", mediumInter)}>
                {showAll ? t("kirtan-lyrics.less") : `${t("kirtan-lyrics.more", { count: data.length - 5 })}`}
              </button>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default KirtanFilterAccordion;
